import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { createDraftOrderWithInvoice } from '@/lib/shopifyAdmin';
import { Plus, Trash2, Copy, ExternalLink, X } from 'lucide-react';

type LineItem = { title: string; quantity: number; price: string };

export default function SendPaymentInline({
  email,
  name,
  phone,
  defaultTitle = 'תכשיר רוקחות אישית',
  note,
  tags,
  onSent,
  onClose,
}: {
  email: string;
  name?: string;
  phone?: string;
  defaultTitle?: string;
  note?: string;
  tags?: string[];
  onSent?: (invoiceUrl: string) => void;
  onClose?: () => void;
}) {
  const [items, setItems] = useState<LineItem[]>([
    { title: defaultTitle, quantity: 1, price: '' },
  ]);
  const [shipping, setShipping] = useState(false);
  const [shippingAmount, setShippingAmount] = useState('30');
  const [extraNote, setExtraNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ name: string; invoiceUrl: string } | null>(null);

  const total =
    items.reduce((sum, li) => sum + (parseFloat(li.price) || 0) * li.quantity, 0) +
    (shipping ? parseFloat(shippingAmount) || 0 : 0);

  const send = async () => {
    if (!email) {
      toast.error('חסר אימייל ללקוחה');
      return;
    }
    const valid = items.filter((li) => li.title.trim() && parseFloat(li.price) > 0);
    if (valid.length === 0) {
      toast.error('יש להוסיף לפחות פריט אחד עם מחיר');
      return;
    }
    setSubmitting(true);
    try {
      const r = await createDraftOrderWithInvoice({
        email,
        customerName: name,
        phone,
        lineItems: valid.map((li) => ({
          title: li.title.trim(),
          quantity: Math.max(1, li.quantity),
          originalUnitPrice: parseFloat(li.price).toFixed(2),
        })),
        shippingAmount: shipping ? parseFloat(shippingAmount) || 0 : 0,
        note: [note, extraNote].filter(Boolean).join('\n\n') || undefined,
        tags,
      });
      setResult({ name: r.name, invoiceUrl: r.invoiceUrl });
      toast.success(`טיוטת הזמנה ${r.name} נשלחה לאימייל`);
      onSent?.(r.invoiceUrl);
    } catch (e) {
      toast.error('שגיאה ביצירת הזמנה', {
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="p-4 bg-white border-2 border-primary/20 space-y-4">
      <div className="flex items-center justify-between">
        <div className="font-medium text-sm">שליחת קישור תשלום ל-{email}</div>
        {onClose && (
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {result ? (
        <div className="space-y-3">
          <div className="text-sm">
            נוצרה טיוטת הזמנה <strong>{result.name}</strong> ונשלח אימייל עם קישור לתשלום.
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                navigator.clipboard.writeText(result.invoiceUrl);
                toast.success('הקישור הועתק');
              }}
            >
              <Copy className="h-3.5 w-3.5 me-1" /> העתקת קישור
            </Button>
            <Button variant="outline" size="sm" asChild>
              <a href={result.invoiceUrl} target="_blank" rel="noreferrer">
                <ExternalLink className="h-3.5 w-3.5 me-1" /> פתיחה
              </a>
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div>
            <Label className="text-xs mb-2 block">פריטים</Label>
            <div className="space-y-2">
              {items.map((li, i) => (
                <div key={i} className="grid grid-cols-[1fr_70px_110px_auto] gap-2 items-center">
                  <Input
                    placeholder="שם הפריט"
                    value={li.title}
                    onChange={(e) =>
                      setItems((prev) =>
                        prev.map((p, idx) => (idx === i ? { ...p, title: e.target.value } : p)),
                      )
                    }
                  />
                  <Input
                    type="number"
                    min="1"
                    value={li.quantity}
                    onChange={(e) =>
                      setItems((prev) =>
                        prev.map((p, idx) =>
                          idx === i ? { ...p, quantity: parseInt(e.target.value) || 1 } : p,
                        ),
                      )
                    }
                  />
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="מחיר ₪"
                    value={li.price}
                    onChange={(e) =>
                      setItems((prev) =>
                        prev.map((p, idx) => (idx === i ? { ...p, price: e.target.value } : p)),
                      )
                    }
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setItems((prev) => prev.filter((_, idx) => idx !== i))}
                    disabled={items.length === 1}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="mt-2"
              onClick={() =>
                setItems((prev) => [...prev, { title: '', quantity: 1, price: '' }])
              }
            >
              <Plus className="h-3.5 w-3.5 me-1" /> הוסף פריט
            </Button>
          </div>

          <div className="flex items-center gap-3">
            <Switch checked={shipping} onCheckedChange={setShipping} />
            <Label className="text-sm">כולל משלוח</Label>
            {shipping && (
              <Input
                type="number"
                step="0.01"
                value={shippingAmount}
                onChange={(e) => setShippingAmount(e.target.value)}
                className="w-28"
              />
            )}
          </div>

          <div>
            <Label className="text-xs">הערה ללקוחה (לא חובה)</Label>
            <Textarea
              rows={2}
              value={extraNote}
              onChange={(e) => setExtraNote(e.target.value)}
              placeholder="לדוגמה: לחיוב לפי השאלון מ-12.6"
            />
          </div>

          <div className="flex justify-between items-center pt-2 border-t">
            <div className="text-sm">
              <span className="text-muted-foreground">סה״כ: </span>
              <span className="font-semibold tabular-nums">
                {total.toLocaleString('he-IL', { style: 'currency', currency: 'ILS' })}
              </span>
            </div>
            <Button onClick={send} disabled={submitting} size="sm">
              {submitting ? 'שולח…' : 'יצירת הזמנה ושליחה'}
            </Button>
          </div>
        </>
      )}
    </Card>
  );
}
